import colorStyleConstants from '@qmulus/ui/styles/exports/color.export.module.scss';
import {
  computeColorLuminance,
  hexToRgbNumberArray,
} from '@qmulus/ui/utils/color.ts';

/**
 * @deprecated
 * These exports have moved. Import them from `@qmulus/ui/utils/color.ts` instead.
 */
export {
  computeColorLuminance,
  getContrastForRgbString,
  hexToRgbNumberArray,
} from '@qmulus/ui/utils/color.ts';

export type TPaletteColor = {
  /** name of the palette color, e.g. `blue-8` */
  name: string,
  /** family of the palette color, e.g. `blue` */
  family: string,
  /** step of the palette color within its family, from 1 to 12; undefined for `white` and `black` */
  step?: number,
  color: string,
};

const PALETTE_EXPORT_KEY = /^color(?<family>[A-Z][a-z]+)(?<step>\d+)?$/;

/** All colors of `$color-map`, read from the Sass export, in the order of the map. */
const PALETTE_COLORS: TPaletteColor[] = Object.entries(colorStyleConstants).flatMap(([
  exportKey,
  color,
]) => {
  const match = PALETTE_EXPORT_KEY.exec(exportKey);
  if (!match?.groups) {
    return [];
  }
  const family = match.groups.family.toLowerCase();
  const step = match.groups.step ? Number(match.groups.step) : undefined;
  const nameSuffix = step === undefined ? '' : `-${step}`;
  return [
    {
      name: `${family}${nameSuffix}`,
      family,
      step,
      color,
    },
  ];
});

const GRAYSCALE_FAMILIES = [
  'white',
  'black',
  'gray',
  'zinc',
];

/** The colored families offered by the color selection. */
const FULL_COLORED_FAMILIES = [
  'blue',
  'green',
  'yellow',
  'cyan',
  'pink',
  'indigo',
  'teal',
  'orange',
  'slate',
  'purple',
  'red',
];

/** Order of the colored families, which decides the colors that charts assign to their series. */
const COLORED_FAMILY_ORDER = [
  ...FULL_COLORED_FAMILIES,
  'amber',
  'brown',
  'emerald',
  'fuchsia',
  'lime',
  'rose',
  'sky',
  'violet',
];

function toPaletteRecord(paletteColors: TPaletteColor[]): Record<string, string> {
  return Object.fromEntries(paletteColors.map((paletteColor) => [
    paletteColor.name,
    paletteColor.color,
  ]));
}

export const colorPaletteGrayscale = toPaletteRecord(
  PALETTE_COLORS.filter((paletteColor) => GRAYSCALE_FAMILIES.includes(paletteColor.family)),
);

export const colorPaletteWithoutGrayscale = toPaletteRecord(
  COLORED_FAMILY_ORDER.flatMap((family) => PALETTE_COLORS.filter((paletteColor) => paletteColor.family === family)),
);

export const colorPalette: Record<string, string> = {
  ...colorPaletteGrayscale,
  ...colorPaletteWithoutGrayscale,
};

const DARKER_GRAY_FIRST_STEP = 8;

/**
 * The colors offered by the color selection: every step of the full colored families, the darker
 * grays and black.
 */
export const QMULUS_COLOR_PALETTE: TPaletteColor[] = PALETTE_COLORS.filter((paletteColor) => {
  if (paletteColor.family === 'black') {
    return true;
  }
  if (paletteColor.step === undefined) {
    return false;
  }
  if (paletteColor.family === 'gray') {
    return paletteColor.step >= DARKER_GRAY_FIRST_STEP;
  }
  return FULL_COLORED_FAMILIES.includes(paletteColor.family);
});

/**
 * A hashmap to quickly look up a palette color by its color value
 */
export const QMULUS_COLOR_PALETTE_BY_COLOR: Map<string, TPaletteColor> = new Map(
  QMULUS_COLOR_PALETTE.map((paletteColor) => {
    return [
      paletteColor.color,
      paletteColor,
    ];
  }),
);

/**
 * Use it for dynamic plots, whose series are only known at runtime. Static plots, whose series are
 * known in advance such as the phases of the CCT diagram, take the chart palette of the theme
 * (`--chart-palette-1` to `--chart-palette-6`) instead.
 */
export function generateRandomColor(): string {
  return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
}

/**
 * Converts an RGB array to a hex color code.
 *
 * @function rgbNumberArrayToHex
 * @param {number[]} rgb - An array containing the RGB values.
 * @returns {string} The hex color code.
 * @throws {Error} If the input is not a valid RGB array.
 *
 * @example
 * // Convert RGB to hex
 * rgbNumberArrayToHex([255, 0, 0]); // "#ff0000"
 * rgbNumberArrayToHex([0, 255, 0]); // "#00ff00"
 * rgbNumberArrayToHex([0, 0, 255]); // "#0000ff"
 */
export function rgbNumberArrayToHex(rgb: number[]): string {
  if (!Array.isArray(rgb) || rgb.length !== 3 || rgb.some((n) => n < 0 || n > 255 || !Number.isInteger(n))) {
    throw new Error('Invalid RGB array');
  }

  return `#${rgb.map((numericColorValue) => numericColorValue.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Converts individual RGB values to a hex color code.
 *
 * @function rgbToHex
 * @param {number} red - The red component (0-255).
 * @param {number} green - The green component (0-255).
 * @param {number} blue - The blue component (0-255).
 * @returns {string} The hex color code.
 * @throws {Error} If any component is out of range or not an integer.
 *
 * @example
 * // Convert RGB to hex
 * rgbToHex(255, 0, 0); // "#ff0000"
 * rgbToHex(0, 255, 0); // "#00ff00"
 * rgbToHex(0, 0, 255); // "#0000ff"
 */
export function rgbToHex(red: number, green: number, blue: number): string {
  return rgbNumberArrayToHex([
    red,
    green,
    blue,
  ]);
}

export const colorPaletteWithoutGrayscaleLuminiscenseList = Object.entries(colorPaletteWithoutGrayscale).map(([
  key,
  value,
]) => ({
  cssVarName: key,
  cssColorValue: value,
  luminance: computeColorLuminance(value),
}));

/**
 * @see https://stackoverflow.com/questions/66123016/interpolate-between-two-colours-based-on-a-percentage-value
 *
 * @param color1 - color in hex format, for example 'fff' or '0cf' etc
 * @param color2 - color in hex format, for example 'fff' or '0cf' etc
 * @param percent - value from 0 to 1 of how much percent
 * @returns the color as six hex digits without a leading '#', like 'ff66cc'
 */
export function lerpColor(color1: string, color2: string, percent: number) {
  const [
    r1,
    g1,
    b1,
  ] = hexToRgbNumberArray(color1);
  const [
    r2,
    g2,
    b2,
  ] = hexToRgbNumberArray(color2);

  const q = 1 - percent;
  const rr = Math.round(r1 * percent + r2 * q);
  const rg = Math.round(g1 * percent + g2 * q);
  const rb = Math.round(b1 * percent + b2 * q);

  return Number((rr << 16) + (rg << 8) + rb).toString(16).padStart(6, '0');
}

/**
 * @param cssColor - for example 'rbg(111,11,90)'
 * @returns
 */
export function extractRGBValues(cssColor: string) {
  const rgbRegex = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/;
  const match = cssColor.match(rgbRegex);
  if (match) {
    return match.slice(1).map(Number); // Convert strings to numbers
  }
  return null; // Return null if no match found
}

/**
 * Converts an opacity value (0-100) into a two-character hexadecimal string
 * representing the equivalent opacity in a 0-255 range for CSS color usage.
 * If the provided value is outside the 0-100 range, it will be clipped.
 *
 * @param {number} opacity - The opacity value between 0 and 100.
 * @returns {string} A two-character hex string (e.g., "00" to "FF").
 *
 * @example
 * // Returns "FF" since 100% opacity equals 255 in hex
 * opacityToHex(100);
 *
 * @example
 * // Returns "80" since 50% opacity equals approximately 128 (80 in hex)
 * opacityToHex(50);
 *
 * @example
 * // Returns "00" because -10 is clipped to 0
 * opacityToHex(-10);
 *
 * @example
 * // Returns "FF" because 150 is clipped to 100
 * opacityToHex(150);
 */
export function opacityToHex(opacity: number): string {
  // Clip the opacity to the range 0 to 100
  const clipped = Math.min(100, Math.max(0, opacity));
  // Convert to a value in the 0-255 range and round to the nearest integer
  const hexValue = Math.round((clipped / 100) * 255);
  // Convert to hex string, ensuring it is two characters long and uppercase
  return hexValue.toString(16).toUpperCase().padStart(2, '0');
}

let colorNormalizationContext: CanvasRenderingContext2D | null = null;

/**
 * Writes any CSS color as a 6-digit hex color, discarding any transparency. Theme tokens are not always
 * hex: Sass writes mixed colors as rgb() with fractional channels, which the hex helpers of this file
 * cannot read. The canvas parses the color the same way the browser does.
 *
 * @example
 * toHexColor('rgb(109.4, 131.8, 131.8)'); // '#6d8484'
 *
 * @example
 * toHexColor('#0f0'); // '#00ff00'
 */
export function toHexColor(cssColor: string): string {
  colorNormalizationContext ??= document.createElement('canvas').getContext('2d')!;
  colorNormalizationContext.fillStyle = '#000000';
  colorNormalizationContext.fillStyle = cssColor;
  const normalizedColor = colorNormalizationContext.fillStyle;
  if (normalizedColor.startsWith('#')) {
    return normalizedColor;
  }
  const channelList = normalizedColor.match(/[\d.]+/g)!.slice(0, 3).map((channel) => Math.round(Number(channel)));
  return rgbNumberArrayToHex(channelList);
}

/**
 * Returns the RGB portion of a hex color, discarding any alpha channel.
 *
 * Accepts 3, 4, 6 or 8 digit hex (with or without a leading '#') and always
 * returns a 6-digit '#rrggbb' string. Use it before appending a fresh
 * opacity/alpha so an already-alpha color does not become an invalid 10-digit
 * value.
 *
 * @param hexColor - hex color string, e.g. '#295bacff' or 'eeef'
 * @returns a 6-digit hex color string, e.g. '#295bac'
 *
 * @example
 * stripAlphaChannel('#295bacff'); // '#295bac'
 *
 * @example
 * stripAlphaChannel('#eeef'); // '#eeeeee'
 */
export function stripAlphaChannel(hexColor: string): string {
  const cleanHex = hexColor.replace(/^#/, '');
  if (cleanHex.length <= 4) {
    const [
      red,
      green,
      blue,
    ] = cleanHex;
    return `#${red}${red}${green}${green}${blue}${blue}`;
  }
  return `#${cleanHex.slice(0, 6)}`;
}

/**
 * Use it for dynamic plots, whose series are only known at runtime. Static plots, whose series are
 * known in advance such as the phases of the CCT diagram, take the chart palette of the theme
 * (`--chart-palette-1` to `--chart-palette-6`) instead.
 *
 * @see https://stackoverflow.com/questions/3426404/create-a-hexadecimal-colour-based-on-a-string-with-javascript
 */
export function stringToColorHex(str: string) {
  let hash = 0;
  str.split('').forEach((char) => {
    hash = char.charCodeAt(0) + ((hash << 5) - hash);
  });
  let colour = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    colour += value.toString(16).padStart(2, '0');
  }
  return colour;
}

/**
 * Creates a color generator function that returns visually distinct colors
 * from the Qmulus palette on each successive call.
 *
 * The generator steps through a filtered subset of the palette (controlled by
 * brightness bounds) using a configurable stride so that adjacent calls produce
 * colors that are far apart in the palette, maximizing visual distinction.
 *
 * When the end of the palette is reached the index wraps around with a small
 * random offset to avoid repeating the exact same sequence.
 *
 * Use it for dynamic plots, whose series are only known at runtime. Static plots, whose series are
 * known in advance such as the phases of the CCT diagram, take the chart palette of the theme
 * (`--chart-palette-1` to `--chart-palette-6`) instead.
 *
 * @param options.step - Stride between picks in the palette (default 3).
 * @param options.minBrightness - Minimum WCAG relative luminance (0–1, default 0).
 * @param options.maxBrightness - Maximum WCAG relative luminance (0–1, default 0.5).
 * @returns A function that returns a new hex color string on each call.
 *
 * @example
 * const getNextColor = getRandomColorGenerator();
 * const color1 = getNextColor(); // e.g. "#3b82f6ff"
 * const color2 = getNextColor(); // e.g. "#22c55eff"
 *
 * @example
 * // Only pick dark colors
 * const getDarkColor = getRandomColorGenerator({ maxBrightness: 0.5 });
 */
export function getRandomColorGenerator(options: {
  step?: number,
  minBrightness?: number,
  maxBrightness?: number,
} = {}): () => string {
  let currentIndex = 0;
  let cycleStartIndex = 0;
  const step = options.step ?? 5;
  const minBrightness = options?.minBrightness ?? 0;
  // TODO(QMU-4568): adjust when theming will be introduced
  const maxBrightness = options.maxBrightness ?? 0.5;
  const filteredPaletteList = colorPaletteWithoutGrayscaleLuminiscenseList.filter((_color) =>
    _color.luminance >= minBrightness
    && _color.luminance <= maxBrightness);
  const palette = filteredPaletteList.length > 0 ? filteredPaletteList : colorPaletteWithoutGrayscaleLuminiscenseList;

  return () => {
    const newColor = palette[currentIndex];
    currentIndex += step;
    if (currentIndex >= palette.length) {
      // increment the starting index for each new cycle
      cycleStartIndex = (cycleStartIndex + 1) % palette.length;
      currentIndex = cycleStartIndex;
    }
    return newColor.cssColorValue;
  };
}

/**
 * Returns a single color from the Qmulus palette.
 *
 * When called with a numeric identifier, the returned color is deterministic —
 * the same ID always produces the same color. This is useful for assigning
 * stable colors to entities (jobs, KPIs, etc.) without maintaining a generator.
 *
 * When called without arguments, a random palette color is returned.
 *
 * Use it for dynamic plots, whose series are only known at runtime. Static plots, whose series are
 * known in advance such as the phases of the CCT diagram, take the chart palette of the theme
 * (`--chart-palette-1` to `--chart-palette-6`) instead.
 *
 * @param identifier - Optional numeric value used to pick a deterministic color.
 * @param options.minBrightness - Minimum WCAG relative luminance (0–1, default 0).
 * @param options.maxBrightness - Maximum WCAG relative luminance (0–1, default 0.5).
 * @returns A hex color string from the palette.
 *
 * @example
 * // Deterministic: same ID always gives same color
 * getColorFromPalette(job.parentJobId);
 *
 * @example
 * // Deterministic with brightness constraint
 * getColorFromPalette(job.id, { minBrightness: 0.5 });
 *
 * @example
 * // Random pick
 * getColorFromPalette();
 */
export function getColorFromPalette(identifier?: number, options: {
  minBrightness?: number,
  maxBrightness?: number,
} = {}): string {
  const minBrightness = options.minBrightness ?? 0;
  // TODO(QMU-4568): adjust when theming will be introduced
  const maxBrightness = options.maxBrightness ?? 0.5;
  const filteredList = colorPaletteWithoutGrayscaleLuminiscenseList.filter((_color) =>
    _color.luminance >= minBrightness
    && _color.luminance <= maxBrightness);

  const palette = filteredList.length > 0 ? filteredList : colorPaletteWithoutGrayscaleLuminiscenseList;

  if (identifier === undefined) {
    const randomIndex = Math.floor(Math.random() * palette.length);
    return palette[randomIndex].cssColorValue;
  }
  const index = ((identifier % palette.length) + palette.length) % palette.length;
  return palette[index].cssColorValue;
}
